import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { DataTable, IconAvatar } from "../../../components/Common/PageWidgets";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import ActivityDetailModal from "../../../components/Common/ActivityDetailModal";


//Import API
import endpoints from "../../../api/endpoints";
import { get } from "../../../api/axios";

//Utils
import { extractErrorMessage } from "../../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";


import whiteArrow from "../../../assets/images/user-dashboard/white-right-arrow.svg";
import eyeIcon from "../../../assets/images/support/eye.svg";
import coinIcon from "../../../assets/images/user-dashboard/voucher.svg";
import starIcon from "../../../assets/images/user-dashboard/star.svg";

/* -------------------------------------------------------------------------
 * AdminProfilePage — admin profile summary + activity history.
 * ---------------------------------------------------------------------- */

const stripHtmlTags = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const cleanActivityText = (value, fallback = "") => {
  const text = stripHtmlTags(value).trim();
  if (!text || text.toLowerCase() === "default" || text.includes("App\\Models")) return fallback;
  return text;
};

const ACTIVITY_ICON = {
  api_login: { icon: starIcon, tone: "teal" },
  default: { icon: coinIcon, tone: "blue" },
};

const getActivityIcon = (activity) => ACTIVITY_ICON[activity?.event] || ACTIVITY_ICON.default;

const activityColumns = (onView) => [
  {
    key: "description",
    label: "Activity",
    width: "3fr",
    render: (row) => {
      const { icon, tone } = getActivityIcon(row);
      return (
        <span className="data-table__row-label" style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>
          <IconAvatar icon={icon} tone={tone} size={32} />
          <span style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            {cleanActivityText(row.description, "Activity recorded")}
          </span>
        </span>
      );
    },
  },
  {
    key: "type",
    label: "Type",
    width: "1fr",
    render: (row) => cleanActivityText(row.type, "Activity"),
  },
  {
    key: "relative_date",
    label: "Date",
    width: "1fr",
    render: (row) => row.date || "Unknown",
  },
  {
    key: "view",
    label: "",
    width: "64px",
    align: "center",
    render: (row) => (
      <button
        type="button"
        onClick={() => onView && onView(row.id)}
        className="view-activity-btn"
        aria-label={`View ${cleanActivityText(row.type, "activity")}`}
      >
        <img src={eyeIcon} alt="" className="view-activity-btn__icon" />
      </button>
    ),
  },
];

const AdminProfilePage = ({ onOpenSettings, onViewActivity }) => {
  document.title = "Admin Profile | Omaha Food Lovers";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [adminProfile, setAdminProfile] = useState(null);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch admin profile from API
  const fetchProfile = useCallback(async () => {
    try {
      const response = await get(endpoints.platformSettingsAccountSecurity);
      
      if (response.data.status) {
        const data = response.data.data;
        
        setAdminProfile({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
        });
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    }
  }, []);

  // Fetch activities from API
  const fetchActivities = useCallback(async () => {
    setLoadingActivities(true);

    try {
      const response = await get(`${endpoints.adminActivityLogs}?page=1&per_page=10`);

      if (response.data && response.data.data) {
        const newActivities = response.data.data || [];
        setActivities(newActivities.slice(0, 10));
      }
    } catch (err) {
      console.error("Failed to fetch activities:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  }, []);

  // Fetch activities on mount
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleViewActivity = async (activityId) => {
    const act = activities.find(a => a.id === activityId);
    if (act) {
      const { icon } = getActivityIcon(act);
      setSelectedActivity({ ...act, icon });
      setIsModalOpen(true);
      try {
        const response = await get(endpoints.adminActivityLog.replace('{{activity_id}}', activityId));
        const detail = response.data?.data || response.data;
        if (detail) setSelectedActivity({ ...detail, icon: getActivityIcon(detail).icon });
      } catch (error) {
        console.error("Failed to fetch activity detail:", error);
      }
    }
    if (onViewActivity) onViewActivity(activityId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedActivity(null);
  };

  return (
    <div className="page-content profile-page">
      <Container fluid>
        <div className="surface-card profile-top-card">
          <div className="profile-header">
            <div>
              <h2 className="profile-title">Admin Profile</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of admin account information and settings.</p>
            </div>
            <Link to="/admin/settings">
              <SecondaryButton label="Profile Settings" variant="orange" icon={whiteArrow} />
            </Link>
          </div>
          <div className="profile-layout">
              <div className="fields-grid profile-fields">
                <CustomInput label="First Name" name="firstName" value={adminProfile?.first_name || ""} placeholder="e.g. Admin" onChange={() => {}} className="white-bg" />
                <CustomInput label="Last Name" name="lastName" value={adminProfile?.last_name || ""} placeholder="e.g. User" onChange={() => {}} className="white-bg" />
                <CustomInput label="Email" name="email" value={adminProfile?.email || ""} onChange={() => {}} className="white-bg" />
              </div>
            </div>
        </div>

        <div className="wallet-section-header">
          <h2 className="wallet-section-header__title">Activity Log</h2>
          <Link to="/admin/profile/activity-logs">
            <SecondaryButton label="View All" variant="orange" icon={whiteArrow} />
          </Link>
        </div>

        {loadingActivities ? (
          <DataTable columns={activityColumns(handleViewActivity)} rows={[]} getRowKey={(row) => row.id} loading={true} />
        ) : activities && activities.length > 0 ? (
          <DataTable
            columns={activityColumns(handleViewActivity)}
            rows={activities.slice(0, 10)}
            getRowKey={(row) => row.id}
          />
        ) : (
          <p className="vouchers-page__empty">No recent activity yet.</p>
        )}
      </Container>

      <ActivityDetailModal
        activity={selectedActivity}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

AdminProfilePage.propTypes = {
  onOpenSettings: PropTypes.func,
  onViewActivity: PropTypes.func,
};

AdminProfilePage.defaultProps = {
  onOpenSettings: () => {},
  onViewActivity: () => {},
};

export default AdminProfilePage;
